import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../config/firebase';
import { userService, ticketService } from '../../services/api.service';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaTicketAlt } from 'react-icons/fa';
import './UserTickets.css';

const UserTickets = () => {
    const [user] = useAuthState(auth);
    const [tickets, setTickets] = useState([]);
    const [activeTab, setActiveTab] = useState('upcoming');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTickets = async () => {
            if (!user) return;
            
            try {
                const data = await userService.getUserTickets(user.uid);
                const sortedTickets = data.sort((a, b) => 
                    new Date(a.event.date) - new Date(b.event.date)
                );
                setTickets(sortedTickets);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTickets();
    }, [user]);

    const filterTickets = () => {
        const now = new Date();
        return tickets.filter(ticket => {
            const eventDate = new Date(ticket.event.date);
            return activeTab === 'upcoming' 
                ? eventDate >= now 
                : eventDate < now;
        });
    };

    const handleDownloadTicket = async (ticketId) => {
        try {
            const blob = await ticketService.downloadTicket(ticketId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ticket-${ticketId}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading ticket:', error);
        }
    };

    if (loading) {
        return <div className="loading-spinner">Loading...</div>;
    }

    return (
        <div className="user-tickets-container">
            <div className="tickets-header">
                <h1>My Tickets</h1>
                <div className="ticket-tabs">
                    <button 
                        className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveTab('upcoming')}
                    >
                        Upcoming Events
                    </button>
                    <button 
                        className={`tab ${activeTab === 'past' ? 'active' : ''}`}
                        onClick={() => setActiveTab('past')}
                    >
                        Past Events
                    </button>
                </div>
            </div>

            <div className="tickets-grid">
                {filterTickets().map(ticket => (
                    <div key={ticket.id} className="ticket-card">
                        <div className="ticket-image">
                            <img src={ticket.event.image_url} alt={ticket.event.title} />
                            <div className="ticket-status">
                                <span className={`status-badge ${ticket.status}`}>
                                    {ticket.status}
                                </span>
                            </div>
                        </div>
                        
                        <div className="ticket-content">
                            <h3>{ticket.event.title}</h3>
                            
                            <div className="ticket-details">
                                <div className="detail-item">
                                    <FaCalendarAlt />
                                    <span>{new Date(ticket.event.date).toLocaleDateString()}</span>
                                </div>
                                <div className="detail-item">
                                    <FaClock />
                                    <span>{ticket.event.time}</span>
                                </div>
                                <div className="detail-item">
                                    <FaMapMarkerAlt />
                                    <span>{ticket.event.venue}</span>
                                </div>
                                <div className="detail-item">
                                    <FaTicketAlt />
                                    <span>Seat: {ticket.seat}</span>
                                </div>
                            </div>

                            <div className="ticket-actions">
                                <button className="view-ticket-btn">
                                    View Ticket
                                </button>
                                {activeTab === 'upcoming' && (
                                    <button 
                                        className="download-ticket-btn"
                                        onClick={() => handleDownloadTicket(ticket.id)}
                                    >
                                        Download PDF
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filterTickets().length === 0 && (
                <div className="no-tickets">
                    <FaTicketAlt className="no-tickets-icon" />
                    <h3>No {activeTab} tickets found</h3>
                    <p>When you buy tickets, they will appear here.</p>
                </div>
            )}
        </div>
    );
};

export default UserTickets; 